/**
 * Canvas-based image zoom engine — draws a picture onto a <canvas> and lets a caller zoom/pan it
 * via wheel, click-drag, pinch, or stepped zoomIn()/zoomOut() calls. Framework-agnostic: it only
 * ever touches the canvas element it's given and the Image it loads internally — no other DOM,
 * no CSS beyond the single optional "zoom out mode" class name the caller supplies. Any feature
 * that needs to zoom a static image into a canvas can use this directly; handlers/imageZoomHandler.js
 * is the DOM-wiring layer built on top of it for the common "swap an <img> for a zoomable canvas"
 * case (see that file for why the split).
 *
 * Ported from GlobalResources14/Default/js/modules/imageZoom.js
 * (global.NewHomeSource.Modules.ImageZoom), decoupled from the global NewHomeSource namespace
 * and its ImageHandler.addJpgParameter dependency — callers here pass an already-resolved image
 * URL. Same pan/zoom/pinch/wheel math, restructured as an ES class with instance fields instead
 * of the legacy IIFE + prototype-function-with-module-closures pattern.
 *
 * @module libraries/imageZoom
 */

const DEFAULT_MAX_ZOOM_LEVEL = 4;
const DEFAULT_SCALE_FACTOR = 0.5;

/**
 * @param {Touch} t1
 * @param {Touch} t2
 * @returns {number} distance in pixels between the two touch points.
 */
function pinchDistance(t1, t2) {
  const dx = t2.clientX - t1.clientX;
  const dy = t2.clientY - t1.clientY;
  return Math.hypot(dx, dy);
}

/**
 * @param {MouseEvent|TouchEvent} event
 * @returns {{x: number, y: number}} clientX/clientY of the mouse, or the first changed touch.
 */
function movement(event) {
  if (event.changedTouches) {
    return {
      x: event.changedTouches[0].clientX,
      y: event.changedTouches[0].clientY,
    };
  }
  return { x: event.clientX, y: event.clientY };
}

export default class ImageZoom {
  /**
   * @param {HTMLCanvasElement} canvas - Canvas to draw the zoomable image into. Its `width`/
   *   `height` properties are read as the initial drawing surface size — set them before
   *   constructing.
   * @param {string} imageUrl - URL of the image to load and draw.
   * @param {Object} [options]
   * @param {number} [options.maxZoomLevel] - Upper zoom bound (1 is unzoomed). Default 4.
   * @param {number} [options.scaleFactor] - Step size used by wheel/zoom()/zoomIn()/zoomOut().
   *   Default 0.5.
   * @param {string} [options.zoomOutModeClass] - CSS class toggled on the canvas while the
   *   latched "zoom out" mode (see zoom()) is active, so a caller can swap a cursor icon. Omit
   *   to skip the class toggle entirely.
   * @param {Function} [options.onZoomChange] - Called with `{ zoomLevel, atMin, atMax }`
   *   whenever the zoom level changes, and once more after the image loads to report the
   *   initial level. Lets a caller enable/disable zoom in/out buttons at the bounds.
   */
  constructor(canvas, imageUrl, options = {}) {
    this.canvas = canvas;
    this.maxZoomLevel = options.maxZoomLevel ?? DEFAULT_MAX_ZOOM_LEVEL;
    this.scaleFactor = options.scaleFactor ?? DEFAULT_SCALE_FACTOR;
    this.zoomOutModeClass = options.zoomOutModeClass ?? null;
    this.onZoomChange = options.onZoomChange ?? null;

    this.picture = new Image();
    this.bounds = { top: NaN, left: NaN, right: NaN, bottom: NaN };
    this.canvasWidth = canvas.width;
    this.canvasHeight = canvas.height;
    this.zoomLevel = 1;
    this.zoomOutMode = false;
    this.dx = 0;
    this.dy = 0;
    this.ratio = 1;
    this.pinch = null;
    this.startObject = null;
    this.touchStart = {};
    this.touchNow = {};
    this.redrawPending = false;
    this.handlers = null;

    this.drawingContext = canvas.getContext('2d');
    this.drawingContext.imageSmoothingEnabled = true;

    this.boundRedraw = this.redraw.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.clear = this.clear.bind(this);
    this.boundOnImageLoaded = this.onImageLoaded.bind(this);

    this.picture.addEventListener('load', this.boundOnImageLoaded);
    this.picture.src = imageUrl;
  }

  clear() {
    this.touchNow.x = null;
    this.touchNow.y = null;
    this.touchStart.x = null;
    this.touchStart.y = null;
    this.startObject = null;
  }

  /**
   * @returns {boolean} true once a touchStart/mousedown has captured a start position.
   */
  isScrolling() {
    if (!this.startObject) {
      return false;
    }
    return this.startObject.x || this.startObject.x === 0 || this.startObject.y || this.startObject.y === 0;
  }

  /**
   * Captures (or recaptures) the pinch baseline used by subsequent move events: initial
   * distance, current zoom level, the canvas bounding rect and the canvas-relative midpoint of
   * the two touches. Reuses the existing pinch object when present so a rebase preserves identity.
   * @param {Touch} t1
   * @param {Touch} t2
   */
  initOrRebasePinch(t1, t2) {
    const rect = this.canvas.getBoundingClientRect();
    const pinch = this.pinch || {};
    pinch.startDistance = pinchDistance(t1, t2);
    pinch.startZoom = this.zoomLevel;
    pinch.rectLeft = rect.left;
    pinch.rectTop = rect.top;
    pinch.centerX = ((t1.clientX + t2.clientX) / 2) - rect.left;
    pinch.centerY = ((t1.clientY + t2.clientY) / 2) - rect.top;
    this.pinch = pinch;
  }

  /**
   * Latches a zoom-out mode that follows the user's traversal of the zoom range. Mode turns ON
   * when the requested level meets or exceeds the max (so subsequent clicks keep zooming out)
   * and turns OFF when the level returns to the min. Between 1 and the max the current mode is
   * preserved, so the mode doesn't flicker mid-traversal.
   * @param {number} requestedLevel - the zoom level requested by the caller (pre-clamp).
   */
  updateZoomOutMode(requestedLevel) {
    if (requestedLevel >= this.maxZoomLevel) {
      this.zoomOutMode = true;
    } else if (requestedLevel <= 1) {
      this.zoomOutMode = false;
    }

    if (!this.zoomOutModeClass) {
      return;
    }
    this.canvas.classList.toggle(this.zoomOutModeClass, this.zoomOutMode);
  }

  /**
   * Reports the current zoom level and whether it's at either bound, so a caller can
   * enable/disable its zoom in/out buttons accordingly.
   */
  notifyZoomChange() {
    this.onZoomChange?.({
      zoomLevel: this.zoomLevel,
      atMin: this.zoomLevel <= 1,
      atMax: this.zoomLevel >= this.maxZoomLevel,
    });
  }

  constrain() {
    const imageHeight = this.picture.height * this.ratio * this.zoomLevel;
    const imageWidth = this.picture.width * this.ratio * this.zoomLevel;
    const canvasHeight = this.canvasHeight - imageHeight;
    const canvasWidth = this.canvasWidth - imageWidth;

    this.bounds.left = Math.min(0, canvasWidth);
    this.bounds.right = Math.max(0, canvasWidth);
    this.bounds.top = Math.min(0, canvasHeight);
    this.bounds.bottom = Math.max(0, canvasHeight);

    this.dx = Math.max(this.bounds.left, Math.min(this.bounds.right, this.dx));
    this.dy = Math.max(this.bounds.top, Math.min(this.bounds.bottom, this.dy));

    if (this.zoomLevel === 1) {
      this.dx = (this.canvasWidth - this.picture.width * this.ratio * this.zoomLevel) / 2;
      this.dy = (this.canvasHeight - this.picture.height * this.ratio * this.zoomLevel) / 2;
    }
  }

  redraw() {
    this.redrawPending = false;
    const dHeight = this.picture.height * this.ratio * this.zoomLevel;
    const dWidth = this.picture.width * this.ratio * this.zoomLevel;

    this.constrain();
    this.drawingContext.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    this.drawingContext.drawImage(
      this.picture,
      0,
      0,
      this.picture.width,
      this.picture.height,
      this.dx,
      this.dy,
      dWidth,
      dHeight
    );
  }

  /**
   * Requests a single redraw on the next animation frame, coalescing multiple calls within the
   * same frame into one. Avoids queueing redundant RAF callbacks during high-frequency gestures
   * such as pinch and pan.
   */
  scheduleRedraw() {
    if (this.redrawPending) {
      return;
    }
    this.redrawPending = true;
    requestAnimationFrame(this.boundRedraw);
  }

  /**
   * Handler of mouse down, touch start and pinch start events. When two or more touches are
   * present, captures the pinch baseline used by subsequent move events.
   * @param {MouseEvent|TouchEvent} event
   */
  onTouchStart(event) {
    if (event.touches && event.touches.length >= 2) {
      event.preventDefault();
      this.initOrRebasePinch(event.touches[0], event.touches[1]);
      this.clear();
      return;
    }

    const move = movement(event);
    this.touchStart = move;
    this.touchNow = move;
    this.startObject = { x: move.x, y: move.y, time: Date.now() };
  }

  /**
   * Handler of mouse, touch and pinch move events. When two or more touches are active, scales
   * the image relative to the pinch baseline, anchored to the live midpoint of the touches.
   * Otherwise pans the image (when zoomed in).
   * @param {MouseEvent|TouchEvent} event
   */
  onTouchMove(event) {
    if (this.pinch && event.touches && event.touches.length >= 2) {
      event.preventDefault();
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      const newDistance = pinchDistance(t1, t2);
      if (this.pinch.startDistance > 0) {
        const centerX = ((t1.clientX + t2.clientX) / 2) - this.pinch.rectLeft;
        const centerY = ((t1.clientY + t2.clientY) / 2) - this.pinch.rectTop;
        const scale = newDistance / this.pinch.startDistance;
        this.pinch.centerX = centerX;
        this.pinch.centerY = centerY;
        this.setZoomLevel(this.pinch.startZoom * scale, centerX, centerY);
      }
      return;
    }

    if (!this.isScrolling() || this.zoomLevel === 1) {
      return;
    }

    event.preventDefault();
    const moveX = this.touchNow.x - this.touchStart.x;
    const moveY = this.touchNow.y - this.touchStart.y;

    this.pan(moveX, moveY);
    this.touchStart = this.touchNow;
    this.touchNow = movement(event);
  }

  /**
   * Handler of mouse up, touch end and pinch end events. When a pinch is in progress and at
   * least two touches remain, the pinch baseline is rebased to the remaining touches so the
   * next move continues smoothly. Otherwise, behaves as a tap-to-zoom for short, low-movement
   * taps.
   * @param {MouseEvent|TouchEvent} event
   */
  onTouchEnd(event) {
    if (this.pinch) {
      event.preventDefault();
      if (event.touches && event.touches.length >= 2) {
        this.initOrRebasePinch(event.touches[0], event.touches[1]);
        return;
      }
      this.pinch = null;
      this.clear();
      return;
    }

    if (!this.startObject || this.startObject.x == null || this.startObject.y == null) {
      this.clear();
      return;
    }

    const move = movement(event);
    const totalMovementInX = Math.abs(move.x - this.startObject.x);
    const totalMovementInY = Math.abs(move.y - this.startObject.y);
    const timeElapsed = Date.now() - this.startObject.time;
    const isClick = !(totalMovementInX > 10 || totalMovementInY > 10 || timeElapsed > 1000);

    if (isClick) {
      event.preventDefault();
      let zoomX = event.offsetX;
      let zoomY = event.offsetY;
      if ((zoomX == null || zoomY == null) && event.changedTouches && event.changedTouches[0]) {
        const rect = this.canvas.getBoundingClientRect();
        zoomX = event.changedTouches[0].clientX - rect.left;
        zoomY = event.changedTouches[0].clientY - rect.top;
      }
      this.zoom(!event.shiftKey, zoomX, zoomY);
    }

    this.clear();
  }

  /**
   * Handler of the mouse wheel event. Wheel direction is taken literally and is intentionally
   * decoupled from the latched zoom-out mode used by click, so a scroll-up always zooms in
   * (until the max) and a scroll-down always zooms out (until the min).
   * @param {WheelEvent} event
   */
  onWheel(event) {
    if (!event.deltaY) {
      return;
    }
    event.preventDefault();
    const step = event.deltaY < 0 ? this.scaleFactor : -this.scaleFactor;
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    this.setZoomLevel(this.zoomLevel + step, x, y);
  }

  /**
   * Does the math that needs the image dimensions, and attaches the gesture listeners. Safe to
   * call more than once (e.g. if a caller reassigns `.picture.src`) — detaches any previous set
   * first.
   */
  onImageLoaded() {
    this.detachGestureListeners();

    const wheelOptions = { passive: false };
    this.handlers = {
      touchStart: this.onTouchStart,
      mouseLeave: this.clear,
      touchMove: this.onTouchMove,
      touchEnd: this.onTouchEnd,
      wheel: this.onWheel,
      wheelOptions,
    };

    this.canvas.addEventListener('mousedown', this.handlers.touchStart, false);
    this.canvas.addEventListener('mouseleave', this.handlers.mouseLeave, false);
    this.canvas.addEventListener('mousemove', this.handlers.touchMove, false);
    this.canvas.addEventListener('mouseup', this.handlers.touchEnd, false);
    this.canvas.addEventListener('wheel', this.handlers.wheel, this.handlers.wheelOptions);
    this.canvas.addEventListener('touchstart', this.handlers.touchStart, false);
    this.canvas.addEventListener('touchmove', this.handlers.touchMove, false);
    this.canvas.addEventListener('touchend', this.handlers.touchEnd, false);

    this.ratio = Math.min(this.canvasWidth / this.picture.width, this.canvasHeight / this.picture.height);
    this.touchStart = {};
    this.scheduleRedraw();
    this.notifyZoomChange();
  }

  detachGestureListeners() {
    if (!this.handlers) {
      return;
    }
    this.canvas.removeEventListener('mousedown', this.handlers.touchStart, false);
    this.canvas.removeEventListener('mouseleave', this.handlers.mouseLeave, false);
    this.canvas.removeEventListener('mousemove', this.handlers.touchMove, false);
    this.canvas.removeEventListener('mouseup', this.handlers.touchEnd, false);
    this.canvas.removeEventListener('wheel', this.handlers.wheel, this.handlers.wheelOptions);
    this.canvas.removeEventListener('touchstart', this.handlers.touchStart, false);
    this.canvas.removeEventListener('touchmove', this.handlers.touchMove, false);
    this.canvas.removeEventListener('touchend', this.handlers.touchEnd, false);
    this.handlers = null;
  }

  /**
   * Moves the drawn image by the given offset.
   * @param {number} x
   * @param {number} y
   */
  pan(x, y) {
    this.dx += x;
    this.dy += y;
    this.scheduleRedraw();
  }

  /**
   * Sets a new canvas size and redraws at the new dimensions, keeping the image centered.
   * @param {number} width
   * @param {number} height
   */
  resize(width, height) {
    this.canvas.width = Math.round(width);
    this.canvas.height = Math.round(height);
    this.canvasWidth = this.canvas.width;
    this.canvasHeight = this.canvas.height;
    this.ratio = Math.min(this.canvasWidth / this.picture.width, this.canvasHeight / this.picture.height);
    this.dx = (this.canvasWidth - this.picture.width * this.ratio * this.zoomLevel) / 2;
    this.dy = (this.canvasHeight - this.picture.height * this.ratio * this.zoomLevel) / 2;
    this.scheduleRedraw();
  }

  /**
   * Zooms to a specific level, anchoring the image point under (x, y) so it stays fixed on
   * screen during the zoom. Used for continuous gestures such as pinch. When the resulting
   * level is 1, the image is recentered and the (x, y) anchor is ignored.
   * @param {number} newLevel - desired zoom level (clamped between 1 and maxZoomLevel).
   * @param {number} [x] - canvas-relative X anchor. Ignored when the resulting level is 1.
   * @param {number} [y] - canvas-relative Y anchor. Ignored when the resulting level is 1.
   */
  setZoomLevel(newLevel, x, y) {
    const clamped = Math.max(1, Math.min(this.maxZoomLevel, newLevel));
    const previous = this.zoomLevel;

    this.updateZoomOutMode(newLevel);

    if (clamped === previous) {
      return;
    }

    this.zoomLevel = clamped;

    if (clamped === 1) {
      this.dx = (this.canvasWidth - this.picture.width * this.ratio * clamped) / 2;
      this.dy = (this.canvasHeight - this.picture.height * this.ratio * clamped) / 2;
    } else if (typeof x === 'number' && typeof y === 'number') {
      const scale = clamped / previous;
      this.dx = x - (x - this.dx) * scale;
      this.dy = y - (y - this.dy) * scale;
    }

    this.constrain();
    this.scheduleRedraw();
    this.notifyZoomChange();
  }

  /**
   * Increases or decreases the zoom by one step, anchored around (x, y) (defaults to canvas
   * center). While zoom-out mode is latched (after reaching max, until returning to min) every
   * call zooms out regardless of isZoomIn.
   * @param {boolean} isZoomIn - true to step up. Ignored while zoom-out mode is latched.
   * @param {number} [x]
   * @param {number} [y]
   */
  zoom(isZoomIn, x, y) {
    const direction = (this.zoomOutMode || !isZoomIn) ? -1 : 1;
    const anchorX = typeof x === 'number' ? x : this.canvasWidth / 2;
    const anchorY = typeof y === 'number' ? y : this.canvasHeight / 2;
    this.setZoomLevel(this.zoomLevel + direction * this.scaleFactor, anchorX, anchorY);
  }

  /**
   * Explicit one-step zoom-in around the canvas center (or the supplied anchor). Unlike
   * `zoom(true)`, this never reverses direction when zoom-out mode is latched, so a "+" button
   * click at max zoom is a no-op rather than a surprising step-down.
   * @param {number} [x]
   * @param {number} [y]
   */
  zoomIn(x, y) {
    const anchorX = typeof x === 'number' ? x : this.canvasWidth / 2;
    const anchorY = typeof y === 'number' ? y : this.canvasHeight / 2;
    this.setZoomLevel(this.zoomLevel + this.scaleFactor, anchorX, anchorY);
  }

  /**
   * Explicit one-step zoom-out around the canvas center (or the supplied anchor). Counterpart
   * to zoomIn for "-" button clicks; bypasses the latched zoom-out mode for symmetry.
   * @param {number} [x]
   * @param {number} [y]
   */
  zoomOut(x, y) {
    const anchorX = typeof x === 'number' ? x : this.canvasWidth / 2;
    const anchorY = typeof y === 'number' ? y : this.canvasHeight / 2;
    this.setZoomLevel(this.zoomLevel - this.scaleFactor, anchorX, anchorY);
  }

  /**
   * Detaches gesture listeners and stops the image load listener. Safe to call once a caller is
   * done with this instance (e.g. tearing down the zoomable canvas).
   */
  destroy() {
    this.detachGestureListeners();
    this.picture.removeEventListener('load', this.boundOnImageLoaded);
    this.picture.src = '';
  }
}
